import {
  CheckEmailAvailabilityParams,
  CheckEmailAvailabilityResponse,
  CreateEmployeeResponse,
  EmployeeCreate,
  EmployeeDetail,
  EmployeeUpdate,
  ListEmployeesParams,
  ListEmployeesResponse,
  ListRolesResponse,
  Me,
  MePasswordUpdate,
  MeUpdate,
} from '@/services/api.types';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import authedQuery from '@/services/authed.query';
import _ from 'lodash';

const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: authedQuery,
  tagTypes: ['User'],
  endpoints: builder => ({
    // GET /v1/me — the caller's own profile (batch 005, me.proto). Caller-scoped:
    // no id in the response, so the cache tag is the static 'ME'.
    getProfile: builder.query<Me, void>({
      query: () => ({
        url: '/v1/me',
        method: 'GET',
      }),
      keepUnusedDataFor: 1,
      providesTags: [{ type: 'User', id: 'ME' }],
    }),
    // PUT /v1/me — full replace of the six editable fields. Empty response;
    // invalidating 'ME' refetches GET /v1/me (per contract). Password rotation
    // is deliberately NOT here — see updatePassword.
    editProfile: builder.mutation<void, MeUpdate>({
      query: (body: MeUpdate) => ({
        url: '/v1/me',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'User', id: 'ME' }],
    }),
    // POST /v1/me/password — rotate the caller's password (batch 005, ADR-4).
    // Success kills every previously issued token; the page guides re-login,
    // so no cache invalidation is useful here.
    updatePassword: builder.mutation<void, MePasswordUpdate>({
      query: (body: MePasswordUpdate) => ({
        url: '/v1/me/password',
        method: 'POST',
        body,
      }),
    }),
    // --- Employee (admin staff surface) — batch 006, employee.proto ---
    //
    // The path key is the idp UUID throughout (ADR-1). Roles cross the wire as
    // SLUGS in both directions — never numeric ids (ADR-7).

    // GET /v1/employees — paged/filtered/sorted list; filtering, sorting and
    // pagination all happen in SQL, and an empty result set is a 200 with an
    // empty array (the legacy 400 "page invalid" is deliberately not carried
    // over).
    //
    // The query string is built HERE rather than handed to RTK Query's
    // `params`, because `roles` and `organization_ids` are repeated proto
    // fields: `params` serializes an array through URLSearchParams, which
    // comma-joins it into a single value. URLSearchParams.append also
    // percent-encodes the '#' that every role slug starts with — a bare '#'
    // would turn the rest of the URL into a fragment and silently drop every
    // parameter after it.
    listEmployees: builder.query<ListEmployeesResponse, ListEmployeesParams>({
      query: (params: ListEmployeesParams) => {
        const search = new URLSearchParams();
        const scalars: [string, unknown][] = [
          ['page', params.page],
          ['per', params.per],
          ['order_by', params.order_by],
          ['order', params.order],
          ['fuzzy', params.fuzzy],
        ];
        scalars.forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            search.append(key, String(value));
          }
        });
        (params.roles ?? []).forEach(slug => search.append('roles', slug));
        (params.organization_ids ?? []).forEach(id =>
          search.append('organization_ids', id)
        );
        const query = search.toString();
        return {
          url: query ? `/v1/employees?${query}` : '/v1/employees',
          method: 'GET',
        };
      },
      providesTags: result =>
        result?.employees
          ? [
              ...result.employees.map(({ idp }) => ({
                type: 'User' as const,
                id: idp,
              })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    // GET /v1/employees/{idp} — the edit form's source of truth, and the ONLY
    // place to learn which organizations THIS employee is bound to
    // (ListOrganizations.bound is the CALLER's own bindings).
    getEmployee: builder.query<EmployeeDetail, string>({
      query: (idp: string) => ({
        url: `/v1/employees/${idp}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 1,
      providesTags: result => [{ type: 'User', id: result?.idp }],
    }),
    // POST /v1/employees — one server-side transaction writes identity, PII,
    // password credential, role members and org bindings; the temporary
    // password is mailed after the commit and never appears in the response.
    createEmployee: builder.mutation<CreateEmployeeResponse, EmployeeCreate>({
      query: (body: EmployeeCreate) => ({
        url: '/v1/employees',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    // PUT /v1/employees/{idp} — desired state, whole row. The server computes
    // the role/binding diff against what it reads inside its own write
    // transaction, so the client always submits the full sets.
    updateEmployee: builder.mutation<void, EmployeeUpdate>({
      query: ({ idp, ...body }: EmployeeUpdate) => ({
        url: `/v1/employees/${idp}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    // GET /v1/roles — the assignable employee roles as slugs. No display
    // names: the labels live in this app's compile-time i18n bundle, and
    // serving them from the BE would regress Danish users to English.
    listRoles: builder.query<ListRolesResponse, void>({
      query: () => ({
        url: '/v1/roles',
        method: 'GET',
      }),
    }),
    // GET /v1/employees/email-availability — a HINT for the form, never the
    // authority. The authority is the unique index behind the write, whose
    // conflict comes back as a 409. `detect_type` does not migrate: this reads
    // employees only.
    checkEmailAvailability: builder.query<
      CheckEmailAvailabilityResponse,
      CheckEmailAvailabilityParams
    >({
      query: params => ({
        url: '/v1/employees/email-availability',
        method: 'GET',
        params: _.omitBy(params, _.isNil),
      }),
    }),
  }),
});

export const {
  useListEmployeesQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useListRolesQuery,
  useLazyCheckEmailAvailabilityQuery,
  useGetProfileQuery,
  useEditProfileMutation,
  useUpdatePasswordMutation,
} = userApi;

export default userApi;
